import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowLeft, AlertTriangle, ExternalLink, MapPin } from 'lucide-react';
import { ALL_CHEESERIES, ALL_ACCOMMODATIONS } from '../lib/data';
import { getRegion } from '../lib/regions';

/**
 * Internal editorial preview.
 *
 * Renders every entry currently flagged `verified: false` so draft
 * candidates can be visually reviewed before publication. This page
 * is NOT linked from the public site and is noindex'd at the document
 * level — treat it as an admin surface, not a reader surface.
 *
 * When a draft is verified:
 *   1. Flip `verified: true` on the source row.
 *   2. Add `lastVerified: 'YYYY-MM-DD'`.
 *   3. Confirm address, hours, and visitor policy against the
 *      producer's own channels.
 *   4. Move the entry out of the "drafts" comment block into the
 *      state section of the source file.
 */
export default function DraftsPage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Drafts — The Curd Compass (internal)';

    // Discourage accidental indexing of the drafts surface.
    let robots = document.querySelector('meta[name="robots"]');
    const hadExisting = Boolean(robots);
    const previousContent = robots?.getAttribute('content') ?? null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex,nofollow');

    return () => {
      document.title = previousTitle;
      if (robots) {
        if (hadExisting && previousContent !== null) {
          robots.setAttribute('content', previousContent);
        } else if (!hadExisting) {
          robots.parentNode?.removeChild(robots);
        }
      }
    };
  }, []);

  const draftCheeseries = ALL_CHEESERIES.filter((c) => !c.verified);
  const draftAccommodations = ALL_ACCOMMODATIONS.filter((a) => !a.verified);

  return (
    <div className="min-h-screen bg-bg font-sans text-ink">
      {/* Loud warning banner — you should never confuse this for the public site. */}
      <div className="bg-amber-500/10 border-b-2 border-amber-500">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <strong className="font-bold">Editorial preview — not published.</strong>{' '}
            These entries carry <code className="px-1.5 py-0.5 bg-amber-100 rounded text-[12px]">verified: false</code>{' '}
            and do not appear on the public site. Every detail is a starting
            point for verification, not gospel — confirm addresses, hours,
            visitor policies, and ownership against the producer&rsquo;s own
            channels before publishing.
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-ink-light hover:text-accent transition-colors mb-8"
        >
          <ArrowLeft size={14} /> Back to the site
        </Link>

        <header className="space-y-4 mb-12 md:mb-16">
          <span className="pill-minimal active">Internal · Drafts</span>
          <h1 className="text-5xl md:text-6xl font-serif text-ink tracking-tight leading-[1.05]">
            Research drafts <span className="italic font-light text-accent">awaiting verification</span>
          </h1>
          <p className="text-lg text-ink-light max-w-2xl leading-relaxed">
            {draftCheeseries.length} cheesery draft{draftCheeseries.length === 1 ? '' : 's'}
            {' '}and {draftAccommodations.length} accommodation draft{draftAccommodations.length === 1 ? '' : 's'}
            {' '}currently staged. Each one needs a manual pass against the
            producer&rsquo;s site (hours, address, visitor policy, current
            ownership) before the <code className="px-1 py-0.5 bg-sidebar rounded text-[13px]">verified</code>{' '}
            flag is flipped.
          </p>
        </header>

        {draftCheeseries.length > 0 && (
          <section className="space-y-6 mb-16">
            <h2 className="text-3xl font-serif text-ink tracking-tight border-b border-border pb-3">
              Cheesery drafts ({draftCheeseries.length})
            </h2>
            <div className="grid gap-6">
              {draftCheeseries.map((c) => {
                const region = getRegion(c.region);
                return (
                  <article
                    key={c.id}
                    className="card-minimal p-6 md:p-8 bg-white/80 space-y-4"
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="space-y-1">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                          {c.state} · {region?.shortTitle ?? c.region}
                        </p>
                        <h3 className="text-2xl md:text-3xl font-serif text-ink tracking-tight">
                          {c.name}
                        </h3>
                      </div>
                      <span className="px-3 py-1 bg-amber-100 text-amber-900 border border-amber-300 rounded-full text-[10px] font-bold uppercase tracking-widest">
                        verified: false
                      </span>
                    </div>

                    <p className="text-[15px] text-ink-light leading-relaxed">
                      {c.description}
                    </p>

                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[13px] pt-2 border-t border-border">
                      <div className="flex gap-2">
                        <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Address</dt>
                        <dd className="text-ink">{c.address}, {c.city}, {c.state}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Coords</dt>
                        <dd className="text-ink font-mono text-[12px]">{c.lat.toFixed(4)}, {c.lng.toFixed(4)}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Policy</dt>
                        <dd className="text-ink">{c.visitorPolicy}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Hours</dt>
                        <dd className="text-ink">{c.hours}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Milk</dt>
                        <dd className="text-ink">{c.milkTypes.join(', ')}</dd>
                      </div>
                      <div className="flex gap-2">
                        <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Styles</dt>
                        <dd className="text-ink">{c.styles.join(', ')}</dd>
                      </div>
                    </dl>

                    <div className="flex flex-wrap gap-3 pt-2">
                      {c.website && (
                        <a
                          href={c.website}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink font-bold hover:text-accent transition-colors"
                        >
                          Official site <ExternalLink size={12} />
                        </a>
                      )}
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(c.name + ' ' + c.city + ' ' + c.state)}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink-light font-bold hover:text-accent transition-colors"
                      >
                        <MapPin size={12} /> Verify on Google Maps
                      </a>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {draftAccommodations.length > 0 && (
          <section className="space-y-6">
            <h2 className="text-3xl font-serif text-ink tracking-tight border-b border-border pb-3">
              Accommodation drafts ({draftAccommodations.length})
            </h2>
            <div className="grid gap-6">
              {draftAccommodations.map((a) => {
                const region = getRegion(a.region);
                return (
                  <article
                    key={a.id}
                    className="card-minimal bg-white/80 overflow-hidden"
                  >
                    {a.image && (
                      <div
                        className="aspect-[3/1] bg-cover bg-center border-b border-border"
                        style={{ backgroundImage: `url(${a.image})` }}
                        aria-label={a.name}
                      />
                    )}
                    <div className="p-6 md:p-8 space-y-4">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="space-y-1">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-accent italic">
                            {a.state} · {region?.shortTitle ?? a.region} · {a.style}
                          </p>
                          <h3 className="text-2xl md:text-3xl font-serif text-ink tracking-tight">
                            {a.name}
                          </h3>
                        </div>
                        <span className="px-3 py-1 bg-amber-100 text-amber-900 border border-amber-300 rounded-full text-[10px] font-bold uppercase tracking-widest">
                          verified: false
                        </span>
                      </div>

                      <p className="text-[15px] text-ink-light leading-relaxed">
                        {a.description}
                      </p>

                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[13px] pt-2 border-t border-border">
                        <div className="flex gap-2">
                          <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Address</dt>
                          <dd className="text-ink">{a.address}, {a.city}, {a.state}</dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Coords</dt>
                          <dd className="text-ink font-mono text-[12px]">{a.lat.toFixed(4)}, {a.lng.toFixed(4)}</dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Price</dt>
                          <dd className="text-ink">{a.priceRange}</dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Hours</dt>
                          <dd className="text-ink">{a.hours}</dd>
                        </div>
                        <div className="flex gap-2 md:col-span-2">
                          <dt className="text-ink-light font-bold uppercase tracking-wider text-[10px] pt-0.5 shrink-0">Amenities</dt>
                          <dd className="text-ink">{a.amenities.join(', ')}</dd>
                        </div>
                      </dl>

                      <div className="flex flex-wrap gap-3 pt-2">
                        {a.website && (
                          <a
                            href={a.website}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink font-bold hover:text-accent transition-colors"
                          >
                            Official site <ExternalLink size={12} />
                          </a>
                        )}
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a.name + ' ' + a.city + ' ' + a.state)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink-light font-bold hover:text-accent transition-colors"
                        >
                          <MapPin size={12} /> Verify on Google Maps
                        </a>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        {draftCheeseries.length === 0 && draftAccommodations.length === 0 && (
          <div className="card-minimal p-12 bg-white/80 text-center">
            <p className="text-lg text-ink-light italic">
              No drafts currently staged. Add research candidates to the data
              files with <code className="px-1.5 py-0.5 bg-sidebar rounded text-[13px]">verified: false</code>{' '}
              and they&rsquo;ll appear here.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
